import React, { useState, useEffect } from 'react'

export default function Galleri() {
  const [pageData, setPage] = useState(null)
  const [galleryCategories, setGalleryCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('all')

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentImagesList, setCurrentImagesList] = useState([])

  useEffect(() => {
    // Set metadata
    document.title = "Galleri - Frejlev Auto v/ Jacob Kvist"
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', "Se billeder fra vores værksted og af vores arbejde hos Frejlev Auto.")
    }

    // Fetch page content and gallery items in parallel
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/by-slug/da/galleri').then(res => res.json()),
      fetch('https://api.dev.auxo.dk/public/content/gallery').then(res => res.json())
    ])
      .then(([page, galleryList]) => {
        setPage(page)
        if (Array.isArray(galleryList)) {
          setGalleryCategories(galleryList)
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching galleri content:', err)
        setLoading(false)
      })
  }, [])

  // Flatten all images for the 'all' category
  const allImages = galleryCategories.reduce((acc, cat) => {
    return [...acc, ...cat.images]
  }, [])

  // Get active images list based on selected category
  const getActiveImages = () => {
    if (activeCategory === 'all') {
      return allImages
    }
    const cat = galleryCategories.find(c => c.category === activeCategory)
    return cat ? cat.images : []
  }

  const openLightbox = (index, imagesList) => {
    setCurrentImageIndex(index)
    setCurrentImagesList(imagesList)
    setLightboxOpen(true)
  }

  const nextImage = (e) => {
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev + 1) % currentImagesList.length)
  }

  const prevImage = (e) => {
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev - 1 + currentImagesList.length) % currentImagesList.length)
  }

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false)
      if (e.key === 'ArrowRight' && lightboxOpen) setCurrentImageIndex((prev) => (prev + 1) % currentImagesList.length)
      if (e.key === 'ArrowLeft' && lightboxOpen) setCurrentImageIndex((prev) => (prev - 1 + currentImagesList.length) % currentImagesList.length)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, currentImagesList])

  const activeImages = getActiveImages()

  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-20 text-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-6 flex items-center gap-2">
          <span>Forside</span>
          <i className="fa-solid fa-chevron-right text-[8px]"></i>
          <span className="text-[#5cbe4f] font-semibold">Galleri</span>
        </div>

        {loading ? (
          <div className="space-y-8 animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-1/2"></div>
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        ) : pageData ? (
          <div className="space-y-10">
            {/* Header */}
            <div className="bg-white p-6 sm:p-10 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-950 tracking-tight">
                {pageData.title.split('|')[0].trim()}
              </h1>
              <div className="h-1 w-20 bg-[#5cbe4f] rounded"></div>

              {/* Description */}
              <div 
                className="rich-text"
                dangerouslySetInnerHTML={{ 
                  __html: pageData.content.replace(/<h1[^>]*>.*?<\/h1>/i, '') 
                }}
              />
            </div>

            {/* Images Grid */}
            {activeImages.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 pt-4">
                {activeImages.map((imgUrl, index) => (
                  <div
                    key={index}
                    onClick={() => openLightbox(index, activeImages)}
                    className="aspect-square overflow-hidden rounded-xl bg-white border border-gray-200 shadow-sm cursor-pointer group relative"
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                      alt="Galleri"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <i className="fa-regular fa-magnifying-glass-plus text-white text-2xl"></i>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white border border-gray-200 rounded-xl shadow-sm">
                <p className="text-gray-500">Der er i øjeblikket ingen billeder i dette galleri.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">Kunne ikke indlæse indholdet. Prøv igen senere.</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && currentImagesList.length > 0 && (
        <div
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-50 bg-black/95 flex flex-col justify-between p-4 md:p-6 animate-in fade-in duration-200"
        >
          {/* Top Bar */}
          <div className="flex items-center justify-between text-white w-full">
            <span className="text-sm font-medium">
              {currentImageIndex + 1} / {currentImagesList.length}
            </span>
            <button
              onClick={() => setLightboxOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
          </div>

          {/* Image Display */}
          <div className="flex-1 flex items-center justify-center relative max-w-5xl mx-auto w-full">
            {/* Prev Button */}
            <button
              onClick={prevImage}
              className="absolute left-2 md:left-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer z-10"
            >
              <i className="fa-solid fa-chevron-left text-lg"></i>
            </button>

            {/* Image */}
            <div className="max-w-full max-h-[75vh] flex items-center justify-center">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${currentImagesList[currentImageIndex].replace('https://assets.auxodev.dk/', '')}`}
                alt="Forstørret visning"
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl select-none"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-2 md:right-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer z-10"
            >
              <i className="fa-solid fa-chevron-right text-lg"></i>
            </button>
          </div>

          {/* Bottom Info */}
          <div className="text-center text-gray-500 text-xs py-4">
            Brug piletasterne til at skifte billede · Klik udenfor for at lukke
          </div>
        </div>
      )}
    </div>
  )
}
